/**
 * Shared look for Carbon fields (DESIGN.md "Inputs / Fields"): a square field
 * fill with a 1px border-strong bottom line, the 2px focus outline drawn
 * inside, and a 2px Coral outline only for blocking validation. A
 * non-component module so the field primitives share one source and
 * react-refresh stays warning-free.
 */
import type { FieldSize } from './types'

/** sm 32px, md 40px (Carbon's default field), lg 48px (a toolbar field). */
export const FIELD_HEIGHT: Record<FieldSize, string> = {
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-12',
}

export const FIELD_BASE =
  'block w-full min-w-0 border-b border-line-strong bg-field px-4 text-[14px] leading-[1.29] tracking-[0.16px] ' +
  'text-text-primary placeholder:text-text-placeholder transition-colors duration-110 ease-carbon ' +
  'focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-focus ' +
  'disabled:cursor-not-allowed disabled:border-transparent disabled:text-text-placeholder'

/** Blocking validation only: the field keeps the Coral outline until focus takes over. */
export const FIELD_INVALID = 'outline outline-2 -outline-offset-2 outline-sev-crit'

/**
 * For a layer-02 surface (a modal, the filter menu): `--field` equals layer-02
 * in dark and the ground in light, so a field there would vanish into it.
 * Carbon steps the field up one layer, as the shell zone already does.
 */
export const FIELD_ON_LAYER_02 = '[--field:var(--layer-selected)] light:[--field:var(--layer-01)]'

/** The ids a field's helper or error text renders under, and the control's aria-describedby. */
export function fieldIds(id: string, helper: unknown, error: unknown) {
  const helperId = `${id}-helper`
  const errorId = `${id}-error`
  const describedBy = error ? errorId : helper ? helperId : undefined
  return { helperId, errorId, describedBy }
}
